import React from 'react';
import { css } from '@emotion/css';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import * as BaseCard from '@/modules/content/components/ContentCard/BaseCard';

import { useBaseCard } from '../../BaseCard/Provider';
import {
  WIDE_CARD_IMAGE_HEIGHT,
  WIDE_CARD_IMAGE_WIDTH,
  WIDE_CARD_MOBILE_IMAGE_HEIGHT,
  WIDE_CARD_MOBILE_IMAGE_WIDTH,
} from '../styles';

export const wideCardImageStyles = css`
  height: ${WIDE_CARD_IMAGE_HEIGHT}rem;
  width: ${WIDE_CARD_IMAGE_WIDTH}rem;
`;

const wideCardImageMobileStyles = css`
  width: ${WIDE_CARD_MOBILE_IMAGE_WIDTH}rem;
  height: ${WIDE_CARD_MOBILE_IMAGE_HEIGHT}rem;
`;

interface WideCardContentParams {
  renderInfo?: () => React.ReactNode;
  imageProps: { width: number; height: number };
  renderImageIcon?: () => React.ReactNode;
  renderName?: () => React.ReactNode;
}

export const WideCardContent = ({
  renderInfo,
  imageProps,
  renderImageIcon,
  renderName,
}: WideCardContentParams) => {
  const { isMobile } = useScreen();
  const { name, isOptionalContent } = useBaseCard();
  const { t } = useTranslation(['Common']);
  return (
    <Stack alignItems="stretch" className="flex max-w-full flex-1 p-8 xs:p-6" direction="row">
      <Box
        className={cn(
          'flex h-full flex-shrink-0',
          isMobile ? wideCardImageMobileStyles : wideCardImageStyles,
        )}>
        <BaseCard.Image
          alt="content image"
          className="h-full w-full"
          containerClassName="rounded flex-shrink-0 h-full w-full"
          height={imageProps.height}
          width={imageProps.width}>
          {renderImageIcon ? renderImageIcon() : null}
        </BaseCard.Image>
      </Box>
      <Stack
        className="flex min-w-0 flex-1 justify-start pt-12 pb-8 ps-20 pe-20 xs:justify-center xs:py-8 xs:ps-12"
        direction="col">
        <BaseCard.Label />
        {renderName ? (
          renderName()
        ) : (
          <BaseCard.Name className="mt-4 flex-shrink xs:mt-0" numberOfLines={isMobile ? 2 : 1} />
        )}
        {isMobile ? null : <BaseCard.Description className="mt-12 flex-shrink" numberOfLines={2} />}
        {isOptionalContent && isMobile ? (
          <Typography className="italic" color="text-text-placeholder" variant="body-short-02">
            {`(${t('Common:Optional')})`}
          </Typography>
        ) : null}
        {renderInfo ? renderInfo() : null}
      </Stack>
    </Stack>
  );
};
